import React from 'react';

import { useTranslation } from 'react-i18next';

import { ChevronIcon, CloseIcon, SortIcon } from '@icons';

import { SORT_ORDER, Category, SortOption, ShopFiltersState } from '@types';

import { Button } from '@components/button';

import { config } from '@/config.ts';

import { CategoryChip } from './category-chip';
import * as Styled from './shop-filters.styled';

type OwnProps = {
	categories: Category[];
	categoryTags?: Record<string, string>;
	filtersState: ShopFiltersState;
	onFiltersChange: (filters: ShopFiltersState) => void;
	isLoading?: boolean;
};

const ShopFilters: React.FC<OwnProps> = ({ categories, categoryTags, filtersState, onFiltersChange, isLoading = false }) => {
	const { t } = useTranslation('main');

	const [isSortDropdownOpen, setIsSortDropdownOpen] = React.useState(false);
	const [isMobileModalOpen, setIsMobileModalOpen] = React.useState(false);

	const isPmBrand = config.BRAND === 'pm';

	const sortOptions: SortOption[] = React.useMemo(
		() => [
			{ label: t('to_increase'), value: SORT_ORDER.ASC },
			{ label: t('to_decrease'), value: SORT_ORDER.DESC }
		],
		[t]
	);

	// Sort categories by position
	const sortedCategories = React.useMemo(() => {
		return [...categories].sort((a, b) => a.position - b.position);
	}, [categories]);

	// Limited categories for mobile (first 3)
	const limitedCategories = React.useMemo(() => {
		return sortedCategories.slice(0, 3);
	}, [sortedCategories]);

	// Group categories by tags for modal
	const categoriesByTag = React.useMemo(() => {
		const grouped: Record<string, Category[]> = {};

		categories.forEach(category => {
			const tag = category.tag || 'other';
			if (!grouped[tag]) {
				grouped[tag] = [];
			}
			grouped[tag].push(category);
		});

		// Sort each group by position
		Object.keys(grouped).forEach(key => {
			grouped[key].sort((a, b) => a.position - b.position);
		});

		return grouped;
	}, [categories]);

	// Tag order for display
	const tagOrder = React.useMemo(() => {
		if (categoryTags) {
			return Object.values(categoryTags);
		}
		return [];
	}, [categoryTags]);

	const handleCategoryToggle = (categoryId: string) => {
		if (isLoading) return;

		const newSelectedCategories = filtersState.selectedCategories.includes(categoryId)
			? filtersState.selectedCategories.filter(id => id !== categoryId)
			: [...filtersState.selectedCategories, categoryId];

		onFiltersChange({
			...filtersState,
			selectedCategories: newSelectedCategories,
			currentPage: 1
		});
	};

	const handleSortChange = (sortOrder: SORT_ORDER) => {
		if (isLoading) return;

		onFiltersChange({
			...filtersState,
			sortOrder,
			currentPage: 1
		});
		setIsSortDropdownOpen(false);
	};

	const handleSortDropdownClick = (e: React.MouseEvent) => {
		if (isLoading) return;
		e.stopPropagation();
		setIsSortDropdownOpen(!isSortDropdownOpen);
	};

	const handleResetFilters = () => {
		if (isLoading) return;

		onFiltersChange({
			selectedCategories: [],
			sortOrder: SORT_ORDER.ASC,
			currentPage: 1
		});
		setIsMobileModalOpen(false);
	};

	const handleApplyMobileFilters = () => {
		setIsMobileModalOpen(false);
	};

	React.useEffect(() => {
		const handleClickOutside = () => {
			setIsSortDropdownOpen(false);
		};

		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, []);

	const hasActiveFilters = filtersState.selectedCategories.length > 0 || filtersState.sortOrder !== SORT_ORDER.ASC;

	return (
		<>
			<Styled.FiltersContainer>
				<Styled.ChipsSection>
					{/* Desktop - all filters */}
					<Styled.DesktopChipsContainer>
						{sortedCategories.map((category, index) => {
							const prevCategory = index > 0 ? sortedCategories[index - 1] : null;
							const shouldShowDivider = prevCategory && prevCategory.tag !== category.tag;

							return (
								<React.Fragment key={category.id}>
									{shouldShowDivider && <Styled.Divider />}
									<CategoryChip
										category={category}
										filtersState={filtersState}
										isLoading={isLoading}
										categoryToggle={handleCategoryToggle}
									/>
								</React.Fragment>
							);
						})}

						{hasActiveFilters && (
							<Styled.ResetFilterContainer>
								<Styled.ChipsDivider>|</Styled.ChipsDivider>
								<Styled.ResetButton $disabled={isLoading} onClick={handleResetFilters}>
									{t('reset_filters')}
									<CloseIcon.Rounded />
								</Styled.ResetButton>
							</Styled.ResetFilterContainer>
						)}
					</Styled.DesktopChipsContainer>

					{/* Mobile - first 3 filters */}
					<Styled.MobileChipsContainer>
						{limitedCategories.map((category, index) => {
							const prevCategory = index > 0 ? limitedCategories[index - 1] : null;
							const shouldShowDivider = prevCategory && prevCategory.tag !== category.tag;

							return (
								<React.Fragment key={category.id}>
									{shouldShowDivider && <Styled.Divider />}
									<CategoryChip
										category={category}
										filtersState={filtersState}
										isLoading={isLoading}
										categoryToggle={handleCategoryToggle}
									/>
								</React.Fragment>
							);
						})}

						{hasActiveFilters && (
							<Styled.ResetFilterContainer>
								<Styled.ChipsDivider>|</Styled.ChipsDivider>
								<Styled.ResetButton $disabled={isLoading} onClick={handleResetFilters}>
									{t('reset_filters')}
									<CloseIcon.Rounded />
								</Styled.ResetButton>
							</Styled.ResetFilterContainer>
						)}
					</Styled.MobileChipsContainer>
				</Styled.ChipsSection>

				<Styled.RightActionsContainer>
					<Styled.SortDropdown $round={!isPmBrand} $disabled={isLoading} onClick={handleSortDropdownClick}>
						{t('product_filter')}
						<ChevronIcon.Down />

						<Styled.DropdownContent $isOpen={isSortDropdownOpen}>
							{sortOptions.map(option => (
								<Styled.DropdownItem
									key={option.value}
									$selected={filtersState.sortOrder === option.value}
									onClick={() => handleSortChange(option.value)}
								>
									{option.label}
								</Styled.DropdownItem>
							))}
						</Styled.DropdownContent>
					</Styled.SortDropdown>

					<Styled.MobileFilterIcon onClick={() => setIsMobileModalOpen(true)}>
						<SortIcon />
					</Styled.MobileFilterIcon>
				</Styled.RightActionsContainer>
			</Styled.FiltersContainer>

			<Styled.FilterModal $isOpen={isMobileModalOpen}>
				<Styled.ModalContainer>
					<Styled.CloseButton onClick={() => setIsMobileModalOpen(false)}>
						<CloseIcon />
					</Styled.CloseButton>

					<Styled.ModalTitle>{t('filters')}</Styled.ModalTitle>

					<Styled.ModalSection>
						{t('sort_by')}
						<Styled.ModalSortOptions>
							{sortOptions.map(option => (
								<Styled.ModalSortItem
									$round={!isPmBrand}
									key={option.value}
									$selected={option.value === filtersState.sortOrder}
									$disabled={isLoading}
									onClick={() => handleSortChange(option.value)}
								>
									{option.label}
								</Styled.ModalSortItem>
							))}
						</Styled.ModalSortOptions>
					</Styled.ModalSection>

					{tagOrder.map((tag, index) => {
						const tagCategories = categoriesByTag[tag];
						if (!tagCategories || tagCategories.length === 0) return null;

						return (
							<Styled.ModalSection key={tag}>
								{tag.charAt(0).toUpperCase() + tag.slice(1)}:
								<Styled.ModalCategoryChips>
									{tagCategories.map(category => (
										<CategoryChip
											key={category.id}
											category={category}
											filtersState={filtersState}
											isLoading={isLoading}
											categoryToggle={handleCategoryToggle}
										/>
									))}
								</Styled.ModalCategoryChips>
							</Styled.ModalSection>
						);
					})}

					{/* Categories without tag or with unknown tag */}
					{Object.keys(categoriesByTag)
						.filter(tag => !tagOrder.includes(tag))
						.map(tag => {
							const tagCategories = categoriesByTag[tag];
							if (!tagCategories || tagCategories.length === 0) return null;

							return (
								<Styled.ModalSection key={tag}>
									{tag === 'other' ? t('Other') : tag.charAt(0).toUpperCase() + tag.slice(1)}:
									<Styled.ModalCategoryChips>
										{tagCategories.map(category => (
											<CategoryChip
												key={category.id}
												category={category}
												filtersState={filtersState}
												isLoading={isLoading}
												categoryToggle={handleCategoryToggle}
											/>
										))}
									</Styled.ModalCategoryChips>
								</Styled.ModalSection>
							);
						})}

					<Styled.ModalButtons>
						<Button fluid color='primary' onClick={handleApplyMobileFilters}>
							{t('apply')}
						</Button>
						<Button fluid color='dark' onClick={handleResetFilters}>
							{t('reset_all')}
						</Button>
					</Styled.ModalButtons>
				</Styled.ModalContainer>
			</Styled.FilterModal>
		</>
	);
};

export default ShopFilters;
